import type { LocationQuery } from "vue-router";
import type { Module } from "vuex";

import type { ComputedDeliverable, ComputedDeliverableUserQAStatus } from "hoxton-types";
import { PLATFORM } from "hoxton-shared/enums/exports";

import type { Deliverable } from "@/types";
import type { MetadataField } from "@/types/types";

import { BannerQaCommentedOnByFilter, BannerQaStatus, BannerQaStatusFilter, BannerQaUserStatus } from "@/enums/banners";
import {
    FilterType,
    LibraryDrawerTab,
    PreviewDrawerTab,
    UrlFilterParams,
    ViewMode,
    ViewModeDefaultPageSize
} from "@/enums/library";
import { PlatformPublishedStatusFilter, CreativeXScoreFilters, PreflightStatusFilters } from "@/enums/platforms";
import { deepClone, formatGroupName, getObjectValue } from "@/utils";
import { generateFilterForCollection, getActiveValuesOfCollectionFromQueryParams } from "@/utils/urls";
import type { LanguageCode } from "@/utils/languages";
import type { RootState } from "@/store/types";

const storeNamespace = "deliverableLibrary";

const DeliverableLibraryInternalAction = {
    SetInitialState: "setInitialState",
    SetMetadataSearchFiltersByFieldId: "setMetadataSearchFiltersByFieldId",
    SyncFilters: "syncFilters",
    UpdateGroupValueFilter: "updateGroupValueFilter",
    UpdateLanguageFilter: "updateLanguageFilter",
    UpdatePublishedStatusFilter: "updatePublishedStatusFilter",
    UpdateSizeFilter: "updateSizeFilter",
    UpdateStatusFilter: "updateStatusFilter",
    UpdateUserStatusFilter: "updateUserStatusFilter",
    UpdateCommentedOnByFilter: "updateCommentedOnByFilter",
    UpdateVisibleToFilter: "updateVisibleToFilter",
    UpdateMiscFilter: "updateMiscFilter",
    UpdateCreativeXScoreFilter: "updateCreativeXScoreFilter",
    UpdateCreativeXStatusFilter: "updateCreativeXStatusFilter",
    UpdateSataliaStatusFilter: "updateSataliaStatusFilter"
};

export const DeliverableLibraryAction = {
    SetDeliverableStatusByIdHash: `${storeNamespace}/setDeliverableStatusByIdHash`,
    UnsetDeliverableStatusByIdHash: `${storeNamespace}/unsetDeliverableStatusByIdHash`,
    SetInitialState: `${storeNamespace}/${DeliverableLibraryInternalAction.SetInitialState}`,
    SyncFilters: `${storeNamespace}/${DeliverableLibraryInternalAction.SyncFilters}`,
    UpdateGroupValueFilter: `${storeNamespace}/${DeliverableLibraryInternalAction.UpdateGroupValueFilter}`,
    UpdateLanguageFilter: `${storeNamespace}/${DeliverableLibraryInternalAction.UpdateLanguageFilter}`,
    UpdateSizeFilter: `${storeNamespace}/${DeliverableLibraryInternalAction.UpdateSizeFilter}`,
    UpdatePublishedStatusFilter: `${storeNamespace}/updatePublishedStatusFilter`,
    UpdateStatusFilter: `${storeNamespace}/updateStatusFilter`,
    UpdateUserStatusFilter: `${storeNamespace}/updateUserStatusFilter`,
    UpdateCommentedOnByFilter: `${storeNamespace}/updateCommentedOnByFilter`,
    UpdateVisibleToFilter: `${storeNamespace}/updateVisibleToFilter`,
    UpdateMiscFilter: `${storeNamespace}/updateMiscFilter`,
    UpdateCreativeXScoreFilter: `${storeNamespace}/updateCreativeXScoreFilter`,
    UpdateCreativeXStatusFilter: `${storeNamespace}/updateCreativeXStatusFilter`,
    UpdateSataliaStatusFilter: `${storeNamespace}/updateSataliaStatusFilter`,
    SetCampaignId: `${storeNamespace}/setCampaignId`,
    SetTableSortedColumnKeys: `${storeNamespace}/setTableSortedColumnKeys`,
    SetTableVisibleColumnKeys: `${storeNamespace}/setTableVisibleColumnKeys`,
    SetCreativeValuesAreVisible: `${storeNamespace}/setCreativeValuesAreVisible`,
    ClearMetadataFieldsByFieldId: `${storeNamespace}/clearMetadataFieldsByFieldId`,
    SetMetadataFieldsByFieldId: `${storeNamespace}/setMetadataFieldsByFieldId`,
    UnsetMetadataFieldsByFieldId: `${storeNamespace}/unsetMetadataFieldsByFieldId`,
    SetPagination: `${storeNamespace}/setPagination`,
    SetPreviewDrawerActiveTab: `${storeNamespace}/setPreviewDrawerActiveTab`,
    SetDrawerActiveTab: `${storeNamespace}/setDrawerActiveTab`,
    SetDrawerDeliverables: `${storeNamespace}/setDrawerDeliverables`,
    SetDrawerIsVisible: `${storeNamespace}/setDrawerIsVisible`,
    SetEditableGroupFilterOptions: `${storeNamespace}/setEditableGroupFilterOptions`,
    SetLanguageFilterOptions: `${storeNamespace}/setLanguageFilterOptions`,
    SetMasterTemplateFilterOptions: `${storeNamespace}/setMasterTemplateFilterOptions`,
    SetMetadataSearchFiltersByFieldId: `${storeNamespace}/setMetadataSearchFiltersByFieldId`,
    UnsetMetadataSearchFiltersByFieldId: `${storeNamespace}/unsetMetadataSearchFiltersByFieldId`,
    ClearMetadataSearchFiltersByFieldId: `${storeNamespace}/clearMetadataSearchFiltersByFieldId`,
    SetViewMode: `${storeNamespace}/setViewMode`
};

export const DeliverableLibraryGetters = {
    allDeliverablesValues: `${storeNamespace}/allDeliverablesValues`,
    commentedOnByFilter: `${storeNamespace}/commentedOnByFilter`,
    computedDeliverableStatus: `${storeNamespace}/computedDeliverableStatus`,
    computedDeliverableIsPublishedToPlatform: `${storeNamespace}/computedDeliverableIsPublishedToPlatform`,
    computedDeliverableIsPublishedToDoubleClick: `${storeNamespace}/computedDeliverableIsPublishedToDoubleClick`,
    computedDeliverableIsPublishedToODC: `${storeNamespace}/computedDeliverableIsPublishedToODC`,
    groupValueFilters: `${storeNamespace}/groupValueFilters`,
    languageFilters: `${storeNamespace}/languageFilters`,
    sizeFilters: `${storeNamespace}/sizeFilters`,
    publishedStatusFilter: `${storeNamespace}/publishedStatusFilter`,
    statusFilter: `${storeNamespace}/statusFilter`,
    userStatusFilter: `${storeNamespace}/userStatusFilter`,
    urlFilterParams: `${storeNamespace}/urlFilterParams`,
    tableColumnLabelsByColumnKey: `${storeNamespace}/tableColumnLabelsByColumnKey`,
    getLoggedInUserStatusForIdHash: `${storeNamespace}/getLoggedInUserStatusForIdHash`,
    VisibleToFilter: `${storeNamespace}/visibleToFilter`,
    miscFilters: `${storeNamespace}/miscFilters`,
    creativeXScoreFilters: `${storeNamespace}/creativeXScoreFilters`,
    creativeXStatusFilters: `${storeNamespace}/creativeXStatusFilters`,
    sataliaStatusFilters: `${storeNamespace}/sataliaStatusFilters`
};

export const DeliverableLibraryInternalGetters = {
    computedDeliverableStatus: "computedDeliverableStatus"
};

export const DeliverableLibraryMutation = {
    UpdateFilter: "updateFilter",
    ResetState: "resetState",
    SetCampaignId: "setCampaignId",
    SetCreativeValuesAreVisible: "setCreativeValuesAreVisible",
    UnsetDeliverableStatusByIdHash: "unsetDeliverableStatusByIdHash",
    SetDeliverableStatusByIdHash: "setDeliverableStatusByIdHash",
    ClearMetadataFieldsByFieldId: "clearMetadataFieldsByFieldId",
    SetMetadataFieldsByFieldId: "setMetadataFieldsByFieldId",
    unsetMetadataFieldsByFieldId: "unsetMetadataFieldsByFieldId",
    SetPagination: "setPagination",
    SetTableSortedColumnKeys: "setTableSortedColumnKeys",
    SetTableVisibleColumnKeys: "setTableVisibleColumnKeys",
    SetPreviewDrawerActiveTab: "setPreviewDrawerActiveTab",
    SetDrawerActiveTab: "setDrawerActiveTab",
    SetDrawerDeliverables: "setDrawerDeliverables",
    SetDrawerIsVisible: "setDrawerIsVisible",
    SetEditableGroupFilterOptions: "setEditableGroupFilterOptions",
    SetLanguageFilterOptions: "setLanguageFilterOptions",
    SetMasterTemplateFilterOptions: "setMasterTemplateFilterOptions",
    SetMetadataSearchFiltersByFieldId: "setMetadataSearchFiltersByFieldId",
    UnsetMetadataSearchFiltersByFieldId: "unsetMetadataSearchFiltersByFieldId",
    ClearMetadataSearchFiltersByFieldId: "clearMetadataSearchFiltersByFieldId",
    SetViewMode: "setViewMode"
};

const generatePaginationDefaults = () => ({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0
});

const viewModeDefault = ViewMode.Grid;
const qaStatusDefault = BannerQaStatusFilter.All;
const qaUserStatusDefault = BannerQaStatusFilter.All;
const qaCommentedOnByDefault = BannerQaCommentedOnByFilter.All;
const publishedStatusDefault = PlatformPublishedStatusFilter.All;
const creativeXScoreDefault = CreativeXScoreFilters.All;
const creativeXStatusDefault = PreflightStatusFilters.All;
const sataliaStatusDefault = PreflightStatusFilters.All;
const visibleToDefault = [] as string[];
const miscDefault = [] as string[];

type FilterTypeType = (typeof FilterType)[keyof typeof FilterType];
type BannerQaStatusType = (typeof BannerQaStatusFilter)[keyof typeof BannerQaStatusFilter];

type BannerQaStatusFilterType = (typeof BannerQaStatusFilter)[keyof typeof BannerQaStatusFilter];
type PlatformPublishedStatusFilterType =
    (typeof PlatformPublishedStatusFilter)[keyof typeof PlatformPublishedStatusFilter];
type BannerQaUserStatusType = (typeof BannerQaUserStatus)[keyof typeof BannerQaUserStatus];
type BannerQaCommentedOnByFilterType = (typeof BannerQaCommentedOnByFilter)[keyof typeof BannerQaCommentedOnByFilter];

type LibraryDrawerTabType = (typeof LibraryDrawerTab)[keyof typeof LibraryDrawerTab];
type ViewModeType = (typeof ViewMode)[keyof typeof ViewMode];

type PlatformType = (typeof PLATFORM)[keyof typeof PLATFORM];

export type State = {
    campaignId: string | null;
    deliverableStatusByIdHash: Record<
        string,
        {
            publishedStatus: ComputedDeliverable["publishedStatus"];
            status: BannerQaStatusType;
            users: ComputedDeliverableUserQAStatus[];
            visibleTo: string[];
        }
    >;
    drawer: {
        activeTab: LibraryDrawerTabType | null;
        deliverables: ComputedDeliverable[] | null;
        isVisible: boolean;
    };
    metadataSearchFiltersByFieldId: Record<string, string>;
    metadataFieldsByFieldId: Record<string, MetadataField>;
    pagination: ReturnType<typeof generatePaginationDefaults>;
    previewDrawerTab: {
        activeTab: (typeof PreviewDrawerTab)[keyof typeof PreviewDrawerTab] | null;
    };
    table: {
        creativeValuesAreVisible: boolean;
        sortedColumnKeys: string[] | null;
        visibleColumnKeys: string[] | null;
    };
    viewMode: ViewModeType;
    filters: {
        type: FilterTypeType;
        values: string[];
        groupName: string;
        groupValues: string[];
    }[];
    filterOptions: {
        editableGroups: {
            label: string;
            name: string;
            options: { value: string; label: string }[];
        }[];
        languages: { value: string; label: string }[];
        masterTemplates: { value: string; label: string; width: number; height: number }[];
    };
};

type Getters = {
    computedDeliverableIsPublishedToPlatform: (idHash: string, platform: PlatformType) => boolean;
    computedDeliverableIsPublishedToDoubleClick: (idHash: string) => boolean;
    computedDeliverableIsPublishedToODC: (idHash: string) => boolean;
    computedDeliverableStatus: (idHash: string) => BannerQaStatusType;
    computedDeliverableIsPublishedToSizmek: (idHash: string) => boolean;
    getLoggedInUserStatusForIdHash: (idHash: string) => BannerQaUserStatusType | null;
    publishedStatusFilter: string;
    commentedOnByFilter: string;
    statusFilter: string;
    userStatusFilter: string;
    groupValueFilters: (groupName?: string) =>
        | string[]
        | {
              type: FilterTypeType;
              values: string[];
              groupName: string;
              groupValues: string[];
          }[];
    sizeFilters: string[];
    languageFilters: string[];
    urlFilterParams: {
        key: string;
        value: string[];
    }[];
    visibleToFilter: string;
    miscFilters: string;
    creativeXScoreFilters: string;
    creativeXStatusFilters: string;
    sataliaStatusFilters: string;
};

const getDefaultState = (): State => ({
    campaignId: null,
    deliverableStatusByIdHash: {},
    drawer: {
        activeTab: null,
        deliverables: [],
        isVisible: false
    },
    metadataSearchFiltersByFieldId: {},
    metadataFieldsByFieldId: {},
    pagination: generatePaginationDefaults(),
    previewDrawerTab: {
        activeTab: null
    },
    table: {
        creativeValuesAreVisible: false,
        sortedColumnKeys: null,
        visibleColumnKeys: null
    },
    viewMode: viewModeDefault,
    filters: [],
    filterOptions: {
        editableGroups: [],
        languages: [],
        masterTemplates: []
    }
});

const deliverableLibraryModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),

    getters: {
        computedDeliverableIsPublishedToPlatform: state => (idHash: string, platform: PlatformType) => {
            const platformXmlFeed = getObjectValue(
                state.deliverableStatusByIdHash,
                `${idHash}.publishedStatus.${platform}.xmlFeedUrl`
            );
            return platformXmlFeed !== undefined && platformXmlFeed !== null;
        },

        computedDeliverableIsPublishedToDoubleClick: (state, getters: Getters) => (idHash: string) =>
            getters.computedDeliverableIsPublishedToPlatform(idHash, PLATFORM.DOUBLECLICK),

        computedDeliverableIsPublishedToODC: (state, getters: Getters) => (idHash: string) =>
            getters.computedDeliverableIsPublishedToPlatform(idHash, PLATFORM.OPENDC),

        computedDeliverableStatus: state => (idHash: string) => {
            const deliverable = state.deliverableStatusByIdHash[idHash];
            if (deliverable?.status) {
                return state.deliverableStatusByIdHash[idHash].status;
            }
            return BannerQaStatus.Draft;
        },

        getLoggedInUserStatusForIdHash(state, getters, rootState) {
            return (idHash: string) => {
                const userId = rootState.auth.me?.id;
                const deliverable = state.deliverableStatusByIdHash[idHash];
                if (!deliverable) {
                    return null;
                }
                const usersStatuses = deliverable.users;
                if (usersStatuses) {
                    const userStatus = usersStatuses.find(status => status && status.user.id === userId);
                    if (userStatus) {
                        return userStatus.status;
                    }
                }
                return null;
            };
        },

        publishedStatusFilter: state => {
            const publishedStatusFilter = state.filters.filter(filter => filter.type === FilterType.PublishedStatus);
            if (publishedStatusFilter.length === 0) {
                return publishedStatusDefault;
            }
            return publishedStatusFilter[0].values[0];
        },

        commentedOnByFilter: state => {
            const commentedOnByFilter = state.filters.filter(filter => filter.type === FilterType.CommentedOnBy);
            if (commentedOnByFilter.length === 0) {
                return qaCommentedOnByDefault;
            }
            return commentedOnByFilter[0].values[0];
        },

        statusFilter: state => {
            const statusFilter = state.filters.filter(filter => filter.type === FilterType.Status);
            if (statusFilter.length === 0) {
                return qaStatusDefault;
            }
            return statusFilter[0].values[0];
        },

        userStatusFilter: state => {
            const userStatusFilter = state.filters.filter(filter => filter.type === FilterType.UserStatus);
            if (userStatusFilter.length === 0) {
                return qaUserStatusDefault;
            }
            return userStatusFilter[0].values;
        },

        groupValueFilters:
            state =>
            (groupName = undefined) => {
                const groupValueFilters = state.filters.filter(filter => filter.type === FilterType.GroupValue);

                if (groupName) {
                    const groupValueFilter = groupValueFilters.filter(filter => filter.groupName === groupName);
                    return groupValueFilter.length === 0 ? [] : [...groupValueFilter[0].groupValues];
                }

                return groupValueFilters;
            },

        sizeFilters: (state, getters, rootState) => {
            const masterTemplates = rootState.campaign.normalized?.masterTemplates;
            const sizeFilters = state.filters.filter(filter => filter.type === FilterType.Size);
            const sizes = sizeFilters.length === 0 ? [] : [...sizeFilters[0].values];

            // if the template is not in the list then we set a high number so that it will come last
            const getTemplateWidth = (id: string) => masterTemplates[id]?.width ?? 100000;

            const sortedSizeFilters = sizes.sort((a, b) => {
                const widthA = getTemplateWidth(a);
                const widthB = getTemplateWidth(b);
                if (widthA === widthB) {
                    return 0;
                }
                return widthA < widthB ? -1 : 1;
            });

            return sortedSizeFilters.reduce((acc, curr) => {
                if (
                    masterTemplates[curr] &&
                    masterTemplates[curr].width === 300 &&
                    masterTemplates[curr].height === 250
                ) {
                    return [curr].concat(acc);
                }
                return acc.concat(curr);
            }, [] as string[]);
        },

        languageFilters: state => {
            const languageFilters = state.filters.filter(filter => filter.type === FilterType.Language);
            return languageFilters.length === 0 ? [] : [...languageFilters[0].values];
        },

        urlFilterParams: (state, getters: Getters) => {
            /*
                If we dont have a campaign ID then we will assume that the library
                is not yet initialised.

                As such we don't bother with attempting to generate any filter params.
            */
            if (!state.campaignId) {
                return [];
            }
            const urlFilterParams = [
                generateFilterForCollection(
                    UrlFilterParams.Language,
                    getters.languageFilters,
                    state.filterOptions.languages.map(({ value }) => value)
                ),
                generateFilterForCollection(
                    UrlFilterParams.Size,
                    getters.sizeFilters,
                    state.filterOptions.masterTemplates.map(({ value }) => value)
                ),
                {
                    key: UrlFilterParams.PublishedStatus,
                    value:
                        getters.publishedStatusFilter === publishedStatusDefault ? [] : [getters.publishedStatusFilter]
                },
                {
                    key: UrlFilterParams.Status,
                    value: getters.statusFilter === qaStatusDefault ? [] : [getters.statusFilter]
                },
                {
                    key: UrlFilterParams.UserStatus,
                    value: getters.userStatusFilter === qaUserStatusDefault ? [] : [getters.userStatusFilter]
                },
                {
                    key: UrlFilterParams.CommentedOnBy,
                    value: getters.commentedOnByFilter === qaCommentedOnByDefault ? [] : [getters.commentedOnByFilter]
                },

                {
                    key: UrlFilterParams.VisibleTo,
                    value: getters.visibleToFilter
                },
                {
                    key: UrlFilterParams.Misc,
                    value: getters.miscFilters
                },
                {
                    key: UrlFilterParams.CreativeXScore,
                    value:
                        getters.creativeXScoreFilters === creativeXScoreDefault ? [] : [getters.creativeXScoreFilters]
                },
                {
                    key: UrlFilterParams.CreativeXStatus,
                    value:
                        getters.creativeXStatusFilters === creativeXStatusDefault
                            ? []
                            : [getters.creativeXStatusFilters]
                },
                {
                    key: UrlFilterParams.SataliaStatus,
                    value: getters.sataliaStatusFilters === sataliaStatusDefault ? [] : [getters.sataliaStatusFilters]
                },

                ...state.filterOptions.editableGroups.map(editableGroup =>
                    generateFilterForCollection(
                        `${UrlFilterParams.GroupValue}:${editableGroup.name}`,
                        getters.groupValueFilters(editableGroup.name) as string[],
                        editableGroup.options.map(({ value }) => value)
                    )
                )
            ];
            return urlFilterParams;
        },

        tableColumnLabelsByColumnKey(state, getters, rootState) {
            const groupLabelsByGroupName = rootState.campaign.normalized.editableGroupIds.reduce(
                (acc, groupName) => {
                    acc[groupName] = formatGroupName(groupName);
                    return acc;
                },
                {} as Record<string, string>
            );

            const metadataFieldLabelsByFieldId = Object.keys(state.metadataFieldsByFieldId).reduce(
                (acc, fieldId) => {
                    acc[fieldId] = state.metadataFieldsByFieldId[fieldId].name;
                    return acc;
                },
                {} as Record<string, string>
            );
            /*
                This object defines the default sort order.
            */
            return {
                status: "QA Status",
                publishedStatus: "Published",
                templateSize: "Ad Size",
                language: "Language",
                ...groupLabelsByGroupName,
                ...metadataFieldLabelsByFieldId
            };
        },

        visibleToFilter: state => {
            const visibleToFilter = state.filters.filter(filter => filter.type === FilterType.VisibleTo);
            if (visibleToFilter.length === 0) {
                return [];
            }
            return visibleToFilter[0].values[0];
        },

        miscFilters: state => {
            const miscFilter = state.filters.filter(filter => filter.type === FilterType.Misc);

            if (miscFilter.length === 0) {
                return [];
            }
            return miscFilter[0].values[0];
        },

        creativeXScoreFilters: state => {
            const scoreFilter = state.filters.filter(filter => filter.type === FilterType.CreativeXScore);

            if (scoreFilter.length === 0) {
                return creativeXScoreDefault;
            }

            return scoreFilter[0].values[0];
        },

        creativeXStatusFilters: state => {
            const creativeXFilter = state.filters.filter(filter => filter.type === FilterType.CreativeXStatus);

            if (creativeXFilter.length === 0) {
                return creativeXStatusDefault;
            }

            return creativeXFilter[0].values[0];
        },

        sataliaStatusFilters: state => {
            const sataliaFilter = state.filters.filter(filter => filter.type === FilterType.SataliaStatus);

            if (sataliaFilter.length === 0) {
                return sataliaStatusDefault;
            }

            return sataliaFilter[0].values[0];
        }
    },

    mutations: {
        resetState(state) {
            state.filters = [];
            state.metadataSearchFiltersByFieldId = {};
            state.pagination = generatePaginationDefaults();
            state.viewMode = viewModeDefault;
            state.table.sortedColumnKeys = null;
            state.table.visibleColumnKeys = null;
        },

        updateFilter(state, filter: State["filters"][number]) {
            const filters = deepClone(state.filters);

            const filterIndex = filters.findIndex(existingFilter => {
                if (filter.type === FilterType.GroupValue) {
                    return existingFilter.groupName === filter.groupName;
                }
                return existingFilter.type === filter.type;
            });

            const filterValues = filter.values || filter.groupValues;

            if (filterValues.length > 0) {
                if (filterIndex === -1) {
                    // add new filter
                    filters.push(filter);
                } else {
                    // update filter
                    filters.splice(filterIndex, 1, filter);
                }
            } else if (filterIndex !== -1) {
                filters.splice(filterIndex, 1);
            }
            state.filters = filters;
        },

        setCampaignId(state, campaignId: State["campaignId"]) {
            state.campaignId = campaignId;
        },

        setPagination(state, pagination: State["pagination"]) {
            state.pagination = pagination;
        },

        setViewMode(state, viewMode: State["viewMode"]) {
            state.viewMode = viewMode;
        },

        setDrawerActiveTab(state, activeTab: State["drawer"]["activeTab"]) {
            state.drawer.activeTab = activeTab;
        },

        setDrawerDeliverables(state, deliverables: ComputedDeliverable[]) {
            state.drawer.deliverables = deliverables;
        },

        setDrawerIsVisible(state, isVisible: State["drawer"]["isVisible"]) {
            state.drawer.isVisible = isVisible;
        },

        setPreviewDrawerActiveTab(state, activeTab: State["previewDrawerTab"]["activeTab"]) {
            state.previewDrawerTab.activeTab = activeTab;
        },

        setTableSortedColumnKeys(state, sortedColumnKeys: string[]) {
            state.table.sortedColumnKeys = [...sortedColumnKeys];
        },

        setTableVisibleColumnKeys(state, visibleColumnKeys: string[]) {
            state.table.visibleColumnKeys = [...visibleColumnKeys];
        },

        setCreativeValuesAreVisible(state, creativeValuesAreVisible: State["table"]["creativeValuesAreVisible"]) {
            state.table.creativeValuesAreVisible = creativeValuesAreVisible;
        },

        setDeliverableStatusByIdHash(state, deliverables: ComputedDeliverable[]) {
            deliverables.forEach(deliverable => {
                const idHash = deliverable.idHash || deliverable.hash;
                const existingDeliverableStatus = state.deliverableStatusByIdHash[idHash] || {};
                if (idHash) {
                    /*
                        As the deliverable data can sometimes come back incomplete from the BE (publishing
                        mutations), or for situations where we only want to set or update a single field,
                        we fall back to any current data we have for the deliverable and then finally
                        to defaults.
                    */
                    /*
                        Because publishedStatus could intentionally be null we do an explicit
                        check for it before allowing us to fall back to previous or default
                        values.
                    */
                    const publishedStatus =
                        deliverable.publishedStatus === null
                            ? deliverable.publishedStatus
                            : deliverable.publishedStatus || existingDeliverableStatus.publishedStatus || null;
                    state.deliverableStatusByIdHash[idHash] = {
                        status: deliverable.status || existingDeliverableStatus.status || BannerQaStatus.Draft,
                        publishedStatus,
                        users: deliverable.users || existingDeliverableStatus.users || [],
                        visibleTo: deliverable.visibleTo || existingDeliverableStatus.visibleTo || []
                    };
                }
            });
        },

        unsetDeliverableStatusByIdHash(state, deliverables: ComputedDeliverable[]) {
            deliverables.forEach(deliverable => {
                delete state.deliverableStatusByIdHash[deliverable.idHash];
            });
        },

        clearMetadataFieldsByFieldId(state) {
            state.metadataFieldsByFieldId = {};
        },

        setMetadataFieldsByFieldId(state, metadataField: MetadataField) {
            state.metadataFieldsByFieldId[metadataField._id] = metadataField;
        },

        unsetMetadataFieldsByFieldId(state, metadataFieldId: string) {
            state.metadataFieldsByFieldId = {
                ...state.metadataFieldsByFieldId
            };
            delete state.metadataFieldsByFieldId[metadataFieldId];
        },

        setEditableGroupFilterOptions(state, options: State["filterOptions"]["editableGroups"]) {
            state.filterOptions.editableGroups = options;
        },

        setLanguageFilterOptions(state, options: State["filterOptions"]["languages"]) {
            state.filterOptions.languages = options;
        },

        setMasterTemplateFilterOptions(state, options: State["filterOptions"]["masterTemplates"]) {
            state.filterOptions.masterTemplates = options;
        },

        setMetadataSearchFiltersByFieldId(state, { fieldId, searchString }: { fieldId: string; searchString: string }) {
            state.metadataSearchFiltersByFieldId = {
                ...state.metadataSearchFiltersByFieldId,
                [fieldId]: searchString
            };
        },

        unsetMetadataSearchFiltersByFieldId(state, fieldId: string) {
            state.metadataSearchFiltersByFieldId = {
                ...state.metadataSearchFiltersByFieldId
            };
            delete state.metadataSearchFiltersByFieldId[fieldId];
        },

        clearMetadataSearchFiltersByFieldId(state) {
            state.metadataSearchFiltersByFieldId = {};
        }
    },

    actions: {
        setDeliverableStatusByIdHash({ commit }, deliverables) {
            commit(DeliverableLibraryMutation.SetDeliverableStatusByIdHash, deliverables);
        },

        unsetDeliverableStatusByIdHash({ commit }, deliverables) {
            commit(DeliverableLibraryMutation.UnsetDeliverableStatusByIdHash, deliverables);
        },

        setInitialState({ commit, dispatch, state, rootState }, query: LocationQuery) {
            const setPageAndViewModeState = () => {
                if (query[UrlFilterParams.Page]) {
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        currentPage: Number(query[UrlFilterParams.Page])
                    });
                }
                if (query[UrlFilterParams.ViewMode]) {
                    commit(DeliverableLibraryMutation.SetViewMode, query[UrlFilterParams.ViewMode]);
                }
                if (state.viewMode === ViewMode.Grid) {
                    /*
                        We only allow a single page size in grid view, so we'll hard code it.
                    */
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: ViewModeDefaultPageSize[ViewMode.Grid]
                    });
                } else if (query[UrlFilterParams.PageSize]) {
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: Number(query[UrlFilterParams.PageSize])
                    });
                } else {
                    /*
                        If we're not in grid mode and we have no page size set
                        in the query param then let's assume that we're in table
                        mode, so we'll set the default table page size.
                    */
                    commit(DeliverableLibraryMutation.SetPagination, {
                        ...state.pagination,
                        pageSize: ViewModeDefaultPageSize[ViewMode.Table]
                    });
                }
            };
            const setFilterState = () => {
                state.filterOptions.editableGroups.forEach(editableGroup => {
                    dispatch(DeliverableLibraryInternalAction.UpdateGroupValueFilter, {
                        groupName: editableGroup.name,
                        groupValues: getActiveValuesOfCollectionFromQueryParams(
                            `${UrlFilterParams.GroupValue}:${editableGroup.name}`,
                            editableGroup.options.map(({ value }) => value),
                            query
                        ) as string[]
                    }).catch(error => {
                        console.error("Failed to dispatch UpdateGroupValueFilter:", error);
                    });
                });

                dispatch(DeliverableLibraryInternalAction.UpdateSizeFilter, {
                    values: getActiveValuesOfCollectionFromQueryParams(
                        UrlFilterParams.Size,
                        state.filterOptions.masterTemplates.map(({ value }) => value),
                        query
                    ) as string[]
                }).catch(error => {
                    console.error("Failed to dispatch UpdateSizeFilter:", error);
                });

                dispatch(DeliverableLibraryInternalAction.UpdateLanguageFilter, {
                    values: getActiveValuesOfCollectionFromQueryParams(
                        UrlFilterParams.Language,
                        state.filterOptions.languages.map(({ value }) => value),
                        query
                    ) as string[]
                }).catch(error => {
                    console.error("Failed to dispatch UpdateLanguageFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdatePublishedStatusFilter,
                    query[UrlFilterParams.PublishedStatus] ?? publishedStatusDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdatePublishedStatusFilter:", error);
                });
                dispatch(
                    DeliverableLibraryInternalAction.UpdateStatusFilter,
                    query[UrlFilterParams.Status] ?? qaStatusDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateStatusFilter:", error);
                });
                // todo split values
                dispatch(
                    DeliverableLibraryInternalAction.UpdateUserStatusFilter,
                    query[UrlFilterParams.UserStatus] ?? qaUserStatusDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateUserStatusFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateCommentedOnByFilter,
                    query[UrlFilterParams.CommentedOnBy] ?? qaCommentedOnByDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateCommentedOnByFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateVisibleToFilter,
                    query[UrlFilterParams.VisibleTo] ?? visibleToDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateVisibleToFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateMiscFilter,
                    query[UrlFilterParams.Misc] ?? miscDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateMiscFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateCreativeXScoreFilter,
                    query[UrlFilterParams.CreativeXScore] ?? creativeXScoreDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateCreativeXScoreFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateCreativeXStatusFilter,
                    query[UrlFilterParams.CreativeXStatus] ?? creativeXStatusDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateCreativeXFilter:", error);
                });

                dispatch(
                    DeliverableLibraryInternalAction.UpdateSataliaStatusFilter,
                    query[UrlFilterParams.SataliaStatus] ?? sataliaStatusDefault
                ).catch(error => {
                    console.error("Failed to dispatch UpdateSataliaStatusFilter:", error);
                });

                Object.keys(state.metadataFieldsByFieldId).forEach(fieldId => {
                    const searchString = query[`${UrlFilterParams.MetadataSearch}:${fieldId}`];
                    if (searchString) {
                        dispatch(DeliverableLibraryInternalAction.SetMetadataSearchFiltersByFieldId, {
                            fieldId,
                            searchString
                        }).catch(error => {
                            console.error("Failed to dispatch SetMetadataSearchFiltersByFieldId:", error);
                        });
                    }
                });
            };
            commit(DeliverableLibraryMutation.ResetState);
            commit(DeliverableLibraryMutation.SetCampaignId, rootState.campaign._id);
            if (query[UrlFilterParams.DeliverableDrawerIsVisible]) {
                commit(DeliverableLibraryMutation.SetDrawerIsVisible, true);
            }
            setPageAndViewModeState();
            setFilterState();
        },

        // this should be a mutation but we need access to the rootState...
        syncFilters({ commit, state, rootState }) {
            /*
                The purpose of this function is to sanitize our filters by removing values that no
                longer exist in the campaign. Why? So that we don't get into a situation where
                we attempt to get data for an item that existed in a filter but is no longer part of
                the campaign, probably causing the app to crash or making unusual things happen.
            */
            state.filters.forEach(filter => {
                switch (filter.type) {
                    case FilterType.GroupValue:
                        if (
                            !filter.groupValues.every(filterGroupValue =>
                                rootState.campaign.editableGroupValueIds.includes(filterGroupValue)
                            )
                        ) {
                            const updated = {
                                ...filter,
                                groupValues: filter.groupValues.filter(filterGroupValue =>
                                    rootState.campaign.editableGroupValueIds.includes(filterGroupValue)
                                )
                            };

                            commit(DeliverableLibraryMutation.UpdateFilter, updated);
                        }

                        break;

                    case FilterType.Language:
                        if (
                            !(filter.values as LanguageCode[]).every(filterLanguage =>
                                rootState.campaign.languages.includes(filterLanguage)
                            )
                        ) {
                            const updated = {
                                ...filter,
                                values: (filter.values as LanguageCode[]).filter(filterLanguage =>
                                    rootState.campaign.languages.includes(filterLanguage)
                                )
                            };

                            commit(DeliverableLibraryMutation.UpdateFilter, updated);
                        }

                        break;

                    case FilterType.Size: {
                        const { masterTemplateIds } = rootState.campaign.normalized;
                        const filterMasterTemplateIdsWhichStillExistInCampaign = filter.values.filter(
                            filterMasterTemplateId => masterTemplateIds.includes(filterMasterTemplateId)
                        );
                        if (filterMasterTemplateIdsWhichStillExistInCampaign.length !== filter.values.length) {
                            const updated = {
                                ...filter,
                                values: filterMasterTemplateIdsWhichStillExistInCampaign
                            };
                            commit(DeliverableLibraryMutation.UpdateFilter, updated);
                        }
                        break;
                    }

                    default:
                }
            });
        },

        updateGroupValueFilter({ commit }, filter: { values: string[] }) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                ...filter,
                type: FilterType.GroupValue
            });
        },

        updateSizeFilter({ commit }, filter: { values: string[] }) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                ...filter,
                type: FilterType.Size
            });
        },

        updatePublishedStatusFilter({ commit }, status: PlatformPublishedStatusFilterType) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.PublishedStatus,
                values: [status]
            });
        },

        updateStatusFilter({ commit }, status: BannerQaStatusFilterType) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.Status,
                values: [status]
            });
        },

        updateUserStatusFilter({ commit }, status: BannerQaUserStatusType) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.UserStatus,
                values: status
            });
        },

        updateCommentedOnByFilter({ commit }, status: BannerQaCommentedOnByFilterType) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.CommentedOnBy,
                values: [status]
            });
        },

        updateLanguageFilter({ commit }, filter: { values: string[] }) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                ...filter,
                type: FilterType.Language
            });
        },

        updateVisibleToFilter({ commit }, status: string[]) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.VisibleTo,
                values: [status]
            });
        },

        updateMiscFilter({ commit }, status: string[]) {
            const stat = Array.isArray(status) ? status : [status];
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.Misc,
                values: [stat]
            });
        },

        updateCreativeXScoreFilter({ commit }, creativeXScore: string) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.CreativeXScore,
                values: [creativeXScore]
            });
        },

        updateCreativeXStatusFilter({ commit }, creativeXStatus: string) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.CreativeXStatus,
                values: [creativeXStatus]
            });
        },

        updateSataliaStatusFilter({ commit }, sataliaStatus: string) {
            commit(DeliverableLibraryMutation.UpdateFilter, {
                type: FilterType.SataliaStatus,
                values: [sataliaStatus]
            });
        },

        setCampaignId({ commit }, campaignId: State["campaignId"]) {
            commit(DeliverableLibraryMutation.SetCampaignId, campaignId);
        },

        setCreativeValuesAreVisible({ commit }, creativeValuesAreVisible: State["table"]["creativeValuesAreVisible"]) {
            commit(DeliverableLibraryMutation.SetCreativeValuesAreVisible, creativeValuesAreVisible);
        },

        clearMetadataFieldsByFieldId({ commit }) {
            commit(DeliverableLibraryMutation.ClearMetadataFieldsByFieldId);
        },

        setMetadataFieldsByFieldId({ commit }, metadataField: MetadataField) {
            commit(DeliverableLibraryMutation.SetMetadataFieldsByFieldId, metadataField);
        },

        unsetMetadataFieldsByFieldId({ commit }, metadataFieldId: string) {
            commit(DeliverableLibraryMutation.unsetMetadataFieldsByFieldId, metadataFieldId);
        },

        setTableSortedColumnKeys({ commit }, sortedColumnKeys: State["table"]["sortedColumnKeys"]) {
            commit(DeliverableLibraryMutation.SetTableSortedColumnKeys, sortedColumnKeys);
        },

        setTableVisibleColumnKeys({ commit }, visibleColumnKeys: State["table"]["visibleColumnKeys"]) {
            commit(DeliverableLibraryMutation.SetTableVisibleColumnKeys, visibleColumnKeys);
        },

        setPagination({ commit }, pagination: State["pagination"]) {
            commit(DeliverableLibraryMutation.SetPagination, pagination);
        },

        setPreviewDrawerActiveTab({ commit }, activeTab: State["previewDrawerTab"]["activeTab"]) {
            commit(DeliverableLibraryMutation.SetPreviewDrawerActiveTab, activeTab);
        },

        setViewMode({ commit }, viewMode: State["viewMode"]) {
            commit(DeliverableLibraryMutation.SetViewMode, viewMode);
        },

        setDrawerActiveTab({ commit }, activeTab: State["drawer"]["activeTab"]) {
            commit(DeliverableLibraryMutation.SetDrawerActiveTab, activeTab);
        },

        setDrawerDeliverables({ commit }, deliverables: Deliverable[]) {
            commit(DeliverableLibraryMutation.SetDrawerDeliverables, deliverables);
        },

        setDrawerIsVisible({ commit }, isVisible: State["drawer"]["isVisible"]) {
            commit(DeliverableLibraryMutation.SetDrawerIsVisible, isVisible);
        },

        setEditableGroupFilterOptions({ commit }, options: State["filterOptions"]["editableGroups"]) {
            commit(DeliverableLibraryMutation.SetEditableGroupFilterOptions, options);
        },

        setLanguageFilterOptions({ commit }, options: State["filterOptions"]["languages"]) {
            commit(DeliverableLibraryMutation.SetLanguageFilterOptions, options);
        },

        setMasterTemplateFilterOptions({ commit }, options: State["filterOptions"]["masterTemplates"]) {
            commit(DeliverableLibraryMutation.SetMasterTemplateFilterOptions, options);
        },

        setMetadataSearchFiltersByFieldId(
            { commit },
            { fieldId, searchString }: { fieldId: string; searchString: string }
        ) {
            commit(DeliverableLibraryMutation.SetMetadataSearchFiltersByFieldId, { fieldId, searchString });
        },

        unsetMetadataSearchFiltersByFieldId({ commit }, fieldId: string) {
            commit(DeliverableLibraryMutation.UnsetMetadataSearchFiltersByFieldId, fieldId);
        },

        clearMetadataSearchFiltersByFieldId({ commit }) {
            commit(DeliverableLibraryMutation.ClearMetadataSearchFiltersByFieldId);
        }
    }
};

export default deliverableLibraryModule;
